import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Form,
  Button,
  Message,
  Label,
  Checkbox,
  Dropdown,
  Icon,
  Divider,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, getCurrentUserId } from '../../api';

const PROFICIENCY_OPTIONS = [
  { key: 'native', value: 'native', text: 'Native speaker' },
  { key: 'professional', value: 'professional', text: 'Professional proficiency' },
  { key: 'conversational', value: 'conversational', text: 'Conversational' },
];

export default function ContributorRegistration() {
  const { token } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [nativeLanguages, setNativeLanguages] = useState([]);
  const [proficiency, setProficiency] = useState({});
  const [professionalExperience, setProfessionalExperience] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const fileInputRef = useRef(null);
  const [certificationAccepted, setCertificationAccepted] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null); // { id, fullname, email }

  // Detect logged-in user
  useEffect(() => {
    const userId = getCurrentUserId();
    if (userId) {
      fetch(`${getApiPath()}/@users/${userId}`, { headers: getAuthHeaders() })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data) {
            setLoggedInUser({ id: userId, fullname: data.fullname, email: data.email });
            setUsername(userId);
            setFullname(data.fullname || '');
          }
        })
        .catch(() => {});
    }
  }, []);

  // Validate invite token on mount
  useEffect(() => {
    async function validateToken() {
      try {
        const res = await fetch(
          `${getApiPath()}/@polyglot/contributors/invite/${token}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setInviteError(body.error || 'Invalid or expired invitation.');
          setLoading(false);
          return;
        }
        const data = await res.json();
        setInvite(data);
        setEmail(data.email);
        // Initialize proficiency for each proposed language
        const prof = {};
        data.proposed_languages.forEach((lang) => {
          prof[lang] = { level: '', years: '' };
        });
        setProficiency(prof);
      } catch (err) {
        setInviteError('Failed to validate invitation. Please try again.');
      }
      setLoading(false);
    }
    validateToken();
  }, [token]);

  const updateProficiency = (lang, field, value) => {
    setProficiency((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Client-side validation
    if (!loggedInUser) {
      if (!username || username.length < 3) {
        setError('Username must be at least 3 characters.');
        return;
      }
      if (!password || password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== passwordConfirm) {
        setError('Passwords do not match.');
        return;
      }
    }
    if (!fullname || fullname.trim().length < 2) {
      setError('Full name is required.');
      return;
    }
    if (nativeLanguages.length === 0) {
      setError('Please select at least one native language.');
      return;
    }
    if (!certificationAccepted) {
      setError('You must accept the self-certification declaration.');
      return;
    }
    if (!agreementAccepted) {
      setError('You must accept the contributors agreement.');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        invite_token: token,
        username: loggedInUser ? loggedInUser.id : username,
        fullname: fullname.trim(),
        email,
        native_languages: nativeLanguages,
        proficiency,
        professional_experience: professionalExperience || null,
        training_acknowledged: true,
        training_evidence: null,
        certification_accepted: certificationAccepted,
        agreement_accepted: agreementAccepted,
      };
      // Logged-in users authenticate via session token; new users provide a password
      if (loggedInUser) {
        payload.use_session = true;
      } else {
        payload.password = password;
      }
      // Encode CV as base64 if provided
      if (cvFile) {
        const buffer = await cvFile.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buffer).reduce((s, b) => s + String.fromCharCode(b), ''),
        );
        payload.cv = {
          data: base64,
          encoding: 'base64',
          'content-type': cvFile.type || 'application/pdf',
          filename: cvFile.name,
        };
      }

      const res = await fetch(
        `${getApiPath()}/@polyglot/contributors/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Registration failed. Please try again.');
        setSubmitting(false);
        return;
      }

      const data = await res.json();

      // Store JWT token as auth_token cookie (matching Volto's pattern)
      if (data.token) {
        document.cookie = `auth_token=${data.token}; path=/; max-age=7200`;
      }

      setSuccess(true);
      // Full page load (not history.push) so Volto SSR picks up the new cookie
      // and initialises Redux userSession state correctly.
      setTimeout(() => {
        window.location.href = '/polyglot/dashboard';
      }, 2000);
    } catch (err) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder />
      </Container>
    );
  }

  if (inviteError) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Invalid Invitation" />
        <Message error icon>
          <Icon name="exclamation triangle" />
          <Message.Content>
            <Message.Header>Invalid Invitation</Message.Header>
            {inviteError}
          </Message.Content>
        </Message>
      </Container>
    );
  }

  if (success) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Registration Complete" />
        <Message success icon>
          <Icon name="check circle" />
          <Message.Content>
            <Message.Header>Registration Complete</Message.Header>
            Welcome, {fullname}! Redirecting to your dashboard...
          </Message.Content>
        </Message>
      </Container>
    );
  }

  const langName = (code) =>
    invite?.proposed_language_names?.[code] || code;

  const languageOptions = (invite?.proposed_languages || []).map((lang) => ({
    key: lang,
    value: lang,
    text: langName(lang),
  }));

  return (
    <Container style={{ paddingTop: '2em', maxWidth: '700px' }}>
      <Helmet title="Contributor Registration" />
      <Header as="h1">
        <Icon name="translate" />
        <Header.Content>
          Contributor Registration
          <Header.Subheader>
            Join the Polyglot Translation Project
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && <Message error>{error}</Message>}

      <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Account */}
        <Segment>
          <Header as="h3">Account</Header>
          {loggedInUser ? (
            <>
              <Message info>
                <Icon name="user" /> Registering as <strong>{loggedInUser.id}</strong> ({loggedInUser.fullname})
              </Message>
              <Form.Input
                label="Email"
                value={email}
                readOnly
                icon="mail"
                iconPosition="left"
              />
              <Form.Input
                label="Full Name"
                value={fullname}
                readOnly
                icon="user"
                iconPosition="left"
              />
            </>
          ) : (
            <>
              <Form.Input
                label="Email"
                value={email}
                readOnly
                icon="mail"
                iconPosition="left"
              />
              <Form.Input
                label="Username"
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                icon="user"
                iconPosition="left"
                required
              />
              <Form.Group widths="equal">
                <Form.Input
                  label="Password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Form.Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Repeat password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Input
                label="Full Name"
                placeholder="Your full name"
                value={fullname}
                onChange={(e) => setFullname(e.target.value)}
                required
              />
            </>
          )}
        </Segment>

        {/* Languages */}
        <Segment>
          <Header as="h3">Assigned Languages</Header>
          <p>You have been invited to contribute translations for:</p>
          <div style={{ marginBottom: '1em' }}>
            {(invite?.proposed_languages || []).map((lang) => (
              <Label key={lang} color="blue" size="large" style={{ marginBottom: '4px' }}>
                {langName(lang)}
              </Label>
            ))}
          </div>
        </Segment>

        {/* Qualifications */}
        <Segment>
          <Header as="h3">Qualifications</Header>

          <Form.Field required>
            <label>Native language(s)</label>
            <Dropdown
              placeholder="Select your native language(s)"
              multiple
              selection
              options={languageOptions}
              value={nativeLanguages}
              onChange={(e, { value }) => setNativeLanguages(value)}
            />
          </Form.Field>

          <Divider hidden />

          <Header as="h4">Proficiency per Language</Header>
          {(invite?.proposed_languages || []).map((lang) => (
            <Form.Group key={lang} widths="equal">
              <Form.Field width={4}>
                <label>{langName(lang)}</label>
              </Form.Field>
              <Form.Field width={8}>
                <Dropdown
                  placeholder="Proficiency level"
                  selection
                  options={PROFICIENCY_OPTIONS}
                  value={proficiency[lang]?.level || ''}
                  onChange={(e, { value }) =>
                    updateProficiency(lang, 'level', value)
                  }
                />
              </Form.Field>
              <Form.Input
                width={4}
                placeholder="Years"
                type="number"
                min="0"
                value={proficiency[lang]?.years || ''}
                onChange={(e) =>
                  updateProficiency(lang, 'years', e.target.value)
                }
              />
            </Form.Group>
          ))}

          <Form.TextArea
            label="Professional Experience"
            placeholder="Describe your translation experience, qualifications, or relevant background (optional)"
            value={professionalExperience}
            onChange={(e) => setProfessionalExperience(e.target.value)}
            rows={3}
          />
          <Form.Field>
            <label>Upload documents (optional — CV, certificates, diplomas. PDF/JPG/PNG, max 5 MB each)
              {invite?.application_cv && !cvFile && (
                <span style={{ color: '#21ba45', fontWeight: 'normal', marginLeft: '0.5em' }}>
                  ("{invite.application_cv}" already on file from your application)
                </span>
              )}
            </label>
            <Button
              basic
              icon="attach"
              content={cvFile ? cvFile.name : 'Choose file'}
              onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
              color={cvFile ? 'green' : undefined}
            />
            {cvFile && (
              <Button
                basic
                icon="delete"
                size="tiny"
                onClick={(e) => { e.preventDefault(); setCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                style={{ marginLeft: '0.5em' }}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && file.size > 5 * 1024 * 1024) {
                  setError('File must be under 5 MB.');
                  return;
                }
                setCvFile(file || null);
              }}
            />
          </Form.Field>
        </Segment>

        {/* Training info */}
        <Segment>
          <Header as="h3">Training</Header>
          <Message info>
            <Icon name="info circle" />
            After registration, you'll complete a short onboarding (three modules,
            ~10 minutes) before your first review session. The training covers the
            translation workflow, quality standards, and tools.
          </Message>
        </Segment>

        {/* Note to Contributors */}
        <Segment>
          <Header as="h3">A Note About This Process</Header>
          <Message>
            <p>
              This project lives at the intersection of <strong>open source</strong> and{' '}
              <strong>regulatory compliance</strong>. We believe these can coexist
              when the boundaries are clear:
            </p>
            <ul>
              <li><strong>Your translations are free.</strong> We dedicate all contributions
                to the public domain under CC0 — anyone can use them, for anything, forever.</li>
              <li><strong>Your identity is recorded.</strong> Not for ownership, but for
                traceability. If a translation error is discovered in a regulated product,
                the audit trail helps find and fix it.</li>
              <li><strong>Your liability is zero within our platform.</strong> LJUSA TIDER holds
                you harmless for translations used within its own platform and services.
                Third-party use of CC0 public domain translations is at the third party's
                own risk.</li>
              <li><strong>The process is controlled.</strong> Because wrong translations in
                medical devices or safety notices can cause harm — not because we doubt you.</li>
            </ul>
            <p>
              The declarations below formalise this. Please read them carefully.
              See <a href="/policies/AGR-TRN-001" target="_blank" rel="noopener noreferrer">AGR-TRN-001</a> for
              the full Contributors Agreement.
            </p>
          </Message>
        </Segment>

        {/* Declarations */}
        <Segment>
          <Header as="h3">Declarations</Header>

          <Message info>
            <Message.Header>Self-Certification</Message.Header>
            <p>{invite?.certification_text}</p>
          </Message>
          <Form.Field>
            <Checkbox
              label="I confirm that the qualifications I have provided are truthful and accurate"
              checked={certificationAccepted}
              onChange={(e, { checked }) => setCertificationAccepted(checked)}
            />
          </Form.Field>

          <Divider />

          <Message info>
            <Message.Header>Public Domain Dedication &amp; Hold Harmless</Message.Header>
            <p>{invite?.agreement_text}</p>
          </Message>
          <Form.Field>
            <Checkbox
              label="I accept the public domain dedication and hold harmless terms"
              checked={agreementAccepted}
              onChange={(e, { checked }) => setAgreementAccepted(checked)}
            />
          </Form.Field>
        </Segment>

        <Button
          primary
          size="large"
          fluid
          type="submit"
          loading={submitting}
          disabled={
            submitting ||
            !certificationAccepted ||
            !agreementAccepted
          }
        >
          <Icon name="check" />
          Complete Registration
        </Button>
      </Form>
    </Container>
  );
}
